import { useCallback } from 'react'
import { RRule } from 'rrule'
import { OpeningHoursObject, Schedule } from '../types/lot.interface'

const daysOfTheWeek = [
    {
        day: 'Mo',
        index: '0'
    },
    {
        day: 'Tu',
        index: '1'
    },
    {
        day: 'We',
        index: '2'
    },
    {
        day: 'Th',
        index: '3'
    },
    {
        day: 'Fr',
        index: '4'
    },
    {
        day: 'Sa',
        index: '5'
    },
    {
        day: 'Su',
        index: '6'
    }
]

export const allDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']

export const daysMap: { [key: string]: string } = {
    MO: 'Mo',
    TU: 'Tu',
    WE: 'We',
    TH: 'Th',
    FR: 'Fr',
    SA: 'Sa',
    SU: 'Su'
}

const getOpeningHoursDay = (openingHours: OpeningHoursObject): { key?: string; text?: string }[] => {
    if (openingHours.daysCount === 1) {
        return [
            {
                key: `days.weekDaysShort.${openingHours.firstDay}`
            }
        ]
    }
    return [
        {
            key: `days.weekDaysShort.${openingHours.firstDay}`
        },
        {
            text: '-'
        },
        {
            key: `days.weekDaysShort.${openingHours.lastDay}`
        }
    ]
}

const parseIso8601Duration = (duration: string) => {
    const dayMatch = duration.match(/(\d+)D/)
    const hourMatch = duration.match(/(\d+)H/)
    const minuteMatch = duration.match(/(\d+)M/)

    const days = dayMatch ? parseInt(dayMatch[1], 10) : 0
    const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0
    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0

    const totalSeconds = days * 86400 + hours * 3600 + minutes * 60
    return totalSeconds
}

// PT1D
// PT15H30M
const parseOpeningHours = (schedule: Schedule): OpeningHoursObject => {
    let rruleString = schedule.rrule.includes('BYHOUR') ? schedule.rrule : `${schedule.rrule};BYHOUR=0`
    rruleString = rruleString.includes('BYMINUTE') ? rruleString : `${rruleString};BYMINUTE=0`

    const rule = RRule.fromString(rruleString)
    const { duration } = schedule

    const byDay = rule.options.byweekday.map((day) => day.toString().substring(0, 2).toUpperCase())
    const byHour = rule.options.byhour[0]
    const byMinute = rule.options.byminute[0]

    const openingTime = `${byHour.toString().padStart(2, '0')}:${byMinute.toString().padStart(2, '0')}`

    const closingTime = new Date(Date.UTC(1970, 0, 1, byHour, byMinute))
    closingTime.setSeconds(closingTime.getSeconds() + parseIso8601Duration(duration))

    const hours = closingTime.getUTCHours()
    const minutes = closingTime.getUTCMinutes()
    const displayHours = hours === 0 && minutes === 0 ? 23 : hours
    const displayMinutes = hours === 0 && minutes === 0 ? 59 : minutes
    const closingTimeString = `${displayHours.toString().padStart(2, '0')}:${displayMinutes
        .toString()
        .padStart(2, '0')}`

    const openingHoursObject: OpeningHoursObject = {
        firstDay: daysOfTheWeek.find((d) => d.index === byDay[0])?.day,
        lastDay: daysOfTheWeek.find((d) => d.index === byDay[byDay.length - 1])?.day,
        openingTime,
        closingTimeString,
        daysIncluded: byDay,
        daysCount: byDay.length
    }
    return openingHoursObject
}

const getTimeDifference = (hourString: string): number => {
    const currentDate = new Date()
    const newDate = new Date()
    const [hour, minute] = hourString.split(':').map(Number)

    newDate.setHours(hour, minute, 0, 0)

    if (currentDate > newDate) {
        return 0
    }

    const differenceMilliseconds = Math.abs(newDate.getTime() - currentDate.getTime())
    const differenceHours = differenceMilliseconds / (1000 * 60 * 60)

    return Math.ceil(differenceHours)
}

const useParseOpeningHours = () => {
    const openingHours = useCallback((schedule: Schedule[]): OpeningHoursObject[] => {
        const objects = schedule.map((schedule) => {
            const openingHoursObject = parseOpeningHours(schedule)
            return {
                ...openingHoursObject,
                fullOpeningHoursList: [
                    ...getOpeningHoursDay(openingHoursObject),
                    {
                        text: openingHoursObject.openingTime
                    },
                    {
                        text: '-'
                    },
                    {
                        text: openingHoursObject.closingTimeString
                    }
                ]
            }
        })
        daysOfTheWeek.forEach((day) => {
            if (objects.some((o) => o.daysIncluded.includes(day.index))) {
                // do nothing for now
            } else {
                objects.push({
                    firstDay: day.day,
                    lastDay: day.day,
                    openingTime: '', // TODO: check this
                    closingTimeString: '', // TODO: check this
                    daysCount: 1,
                    daysIncluded: [day.index],
                    fullOpeningHoursList: [
                        {
                            key: `days.weekDaysPlural.${day.day}`
                        },
                        {
                            key: 'days.closed'
                        }
                    ]
                })
            }
        })
        return objects
    }, [])

    const getDateOpeningHour = useCallback((date: Date, openingHours?: OpeningHoursObject[]): number => {
        if (openingHours === undefined) return 0
        const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1
        const day = daysOfTheWeek.find((d) => d.index === dayIndex.toString())
        if (day) {
            const closingHour = openingHours.find((o) => o.daysIncluded.includes(day.index))?.closingTimeString
            return closingHour ? getTimeDifference(closingHour) : 0
        }
        return 0
    }, [])

    const isAfterOpeningHour = useCallback((openingHours?: OpeningHoursObject[]): boolean => {
        if (openingHours === undefined) return false
        const currentDate = new Date()
        const dayIndex = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1
        const day = daysOfTheWeek.find((d) => d.index === dayIndex.toString())
        if (day) {
            const openingHour = openingHours.find((o) => o.daysIncluded.includes(day.index))?.openingTime
            if (!openingHour) return false
            const [hour, minute] = openingHour.split(':').map(Number)
            const newDate = new Date()
            newDate.setHours(hour, minute, 0, 0)
            return currentDate >= newDate
        }
        return false
    }, [])

    const getStartAndEndHourOfDay = useCallback(
        (
            openingHours: OpeningHoursObject[] | undefined
        ): {
            start_time: string
            end_time: string
        } => {
            const currentDay = new Date().getDay()
            const currentDayIndex = currentDay === 0 ? 6 : currentDay - 1
            const currentDayObject = daysOfTheWeek.find((d) => d.index === currentDayIndex.toString())
            const currentDayOpeningHours = openingHours?.find((o) =>
                o.daysIncluded.includes(currentDayObject?.index || '0')
            )
            return {
                start_time: currentDayOpeningHours?.openingTime || '',
                end_time: currentDayOpeningHours?.closingTimeString || ''
            }
        },
        []
    )

    return { openingHours, getDateOpeningHour, isAfterOpeningHour, getStartAndEndHourOfDay }
}

export default useParseOpeningHours
